import React, { forwardRef, useState } from 'react';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import ProcSection from './ProcSection';
import { useLoad } from '../../hooks/useLoad';
import { useNotifications } from '../NotificationProvider';
import { fetchReceiving, receiveGoods } from '../../services/ProcurementService';
import { procurementErrorMessage } from '../../utils/procurementErrors';
import { fmtQty, qty } from './procurementLabels';
import type { ReceivingLine, ReceivingView } from '../../types/Procurement';

interface Props {
  orderId: string;
  canEdit: boolean;
  /** Called after goods were received, so the order (status, quantities) can be re-read. */
  onReceived: () => void;
}

/** A key that makes a retried receive write nothing twice. */
export const newIdempotencyKey = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `rcv-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Over-receipt check done before sending: the server enforces it too (400 over_receipt). */
export function overReceiptLines(lines: ReceivingLine[], entered: Record<string, string>): ReceivingLine[] {
  return lines.filter(l => qty(entered[l.item_id]) > qty(l.remaining));
}

/**
 * Receive goods against an order: ordered / received / remaining per line,
 * a quantity box per line, and "Receive all remaining". Receiving more than is
 * remaining is refused, here first and by the server.
 */
const ReceivingPanel = forwardRef<HTMLElement, Props>(({ orderId, canEdit, onReceived }, ref) => {
  const { showSuccess, showError } = useNotifications();
  const { data, loading, error, reload, setData } = useLoad<ReceivingView>(() => fetchReceiving(orderId), [orderId]);
  const [entered, setEntered] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const apply = async (input: Parameters<typeof receiveGoods>[1], done: string) => {
    setSaving(true);
    setFormError(null);
    try {
      const view = await receiveGoods(orderId, { ...input, idempotency_key: newIdempotencyKey() });
      setData(view);
      setEntered({});
      showSuccess(done);
      onReceived();
    } catch (err) {
      const message = procurementErrorMessage(err, 'Could not record the goods received. Please try again.');
      setFormError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const receiveEntered = () => {
    if (!data) return;
    const over = overReceiptLines(data.lines, entered);
    if (over.length > 0) {
      setFormError(`You cannot receive more than remains: check ${over.map(l => l.product_name).join(', ')}.`);
      return;
    }
    const lines = data.lines
      .map(l => ({ item_id: l.item_id, quantity: qty(entered[l.item_id]) }))
      .filter(l => l.quantity > 0);
    if (lines.length === 0) {
      setFormError('Enter a quantity for at least one line.');
      return;
    }
    void apply({ lines }, 'Goods received');
  };

  const anyRemaining = !!data && data.lines.some(l => qty(l.remaining) > 0);

  return (
    <ProcSection
      ref={ref}
      title="Receiving"
      action={canEdit && data ? (
        <Button variant="secondary" size="sm" disabled={!anyRemaining || saving} onClick={() => void apply({ all_remaining: true }, 'Everything remaining was received')}>
          Receive all remaining
        </Button>
      ) : undefined}
    >
      {error ? (
        <LoadErrorBox what="receiving" error={error} onRetry={reload} retrying={loading} />
      ) : !data ? (
        <p className="text-subtle">Loading receiving...</p>
      ) : data.lines.length === 0 ? (
        <p className="text-subtle">This order has no lines to receive yet.</p>
      ) : (
        <>
          <div className="proc-scroll">
            <table className="proc-table">
              <thead>
                <tr>
                  <th>Product</th><th className="proc-num">Ordered</th><th className="proc-num">Received</th><th className="proc-num">Remaining</th>
                  {canEdit && <th className="proc-num">Receive now</th>}
                </tr>
              </thead>
              <tbody>
                {data.lines.map(line => (
                  <tr key={line.item_id}>
                    <td>{line.product_name}{line.pack_size ? <span className="text-subtle"> ({fmtQty(line.pack_size)} {line.pack_unit ?? ''}/pack)</span> : null}</td>
                    <td className="proc-num">{fmtQty(line.quantity)}</td>
                    <td className="proc-num">{fmtQty(line.quantity_received)}</td>
                    <td className="proc-num">{fmtQty(line.remaining)}</td>
                    {canEdit && (
                      <td className="proc-num">
                        <input
                          className="proc-qty-input"
                          type="number"
                          min="0"
                          inputMode="decimal"
                          aria-label={`Receive now: ${line.product_name}`}
                          disabled={qty(line.remaining) <= 0 || saving}
                          value={entered[line.item_id] ?? ''}
                          onChange={e => { setFormError(null); setEntered({ ...entered, [line.item_id]: e.target.value }); }}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {formError && <p role="alert" className="form-error-text">{formError}</p>}
          {canEdit && (
            <div className="proc-actions">
              <Button variant="primary" size="sm" loading={saving} onClick={receiveEntered}>Receive these quantities</Button>
            </div>
          )}
        </>
      )}
    </ProcSection>
  );
});
ReceivingPanel.displayName = 'ReceivingPanel';

export default ReceivingPanel;
